/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import * as React from 'react';
import { useIntl } from 'react-intl';

import { FlatTableRow, FlatTableRowHeader } from 'carbon-react/lib/components/flat-table';
import Pill from 'carbon-react/lib/components/pill';

import { DragHandle, useTreeRow } from '@futrli/ui-components';
import { formatCurrency } from '@futrli/localisation';

import { AccountCategory } from '../../../forecast/prediction-builder/types';
import { AccountRowActions } from './account-row-actions';
import { ActiveMode, COA_TYPE, COADisplayAccount } from '../../types';
import { DaysToPaySwitchRule } from './days-to-pay-switch-rule';
import { DragHandlers } from '../../hooks/useCategoryDragDrop';
import { useCoaActions } from '../context/coa-actions-context';
import { useLocalisedConstantsContext } from '../../../../../localisation/src/core/localised-constants-context';
import { usePredictManagementContext } from '../../../predict-management/context/predict-management-context';

interface Props {
  row: COADisplayAccount;
  isBalanceSheet: boolean;
  accountNameMap?: Record<string, string>;
  isNested?: boolean;
  activeMode?: ActiveMode;
  categoryName?: string;
  drag: DragHandlers;
  index: number;
  isUpdatingStructure: boolean;
}

export const AccountRow = ({
  row,
  isBalanceSheet,
  accountNameMap,
  isNested,
  activeMode,
  categoryName,
  drag,
  index,
  isUpdatingStructure
}: Props) => {
  const intl = useIntl();
  const { currency } = usePredictManagementContext();
  const { accountCategoryNames } = useLocalisedConstantsContext();
  const { handleSwitchRuleClick } = useCoaActions();

  const isReorderingMode = !!(activeMode === ActiveMode.REORDERING && drag && categoryName);

  const { rowRef, indentPx } = useTreeRow({
    depth: row.depth || 0,
    drag,
    index,
    isReordering: isReorderingMode,
    type: row.type
  });

  return (
    <FlatTableRow
      bgColor={isNested ? '#F4F5F6' : undefined}
      key={row.id}
      ref={rowRef}
      id={`${row.type}-${index}-${categoryName}`}
      data-testid={`${row.type}-${index}-${categoryName}`}
      data-element={isUpdatingStructure ? 'updating-coa-row' : undefined}
    >
      {isReorderingMode ? (
        <FlatTableRowHeader pl={`${indentPx}px`} data-testid="coa-account-name">
          <div style={{ display: 'flex', alignItems: 'center', height: '20px' }}>
            <DragHandle drag={drag} index={index} rowRef={rowRef} disabled={isUpdatingStructure} />
            <span style={{ marginLeft: '8px' }}>{accountNameMap?.[row.id] || ''}</span>
            {row.recategorised_from && (
              <Pill pillRole="status" colorVariant="information" fill ml={1}>
                {intl.formatMessage(
                  {
                    id: 'common.reclassified_from',
                    defaultMessage: 'Reclassified from {categoryName}'
                  },
                  { categoryName: accountCategoryNames[row.recategorised_from as AccountCategory] }
                )}
              </Pill>
            )}
          </div>
        </FlatTableRowHeader>
      ) : (
        <FlatTableRowHeader pl={`${indentPx}px`} data-testid="coa-account-name">
          <>
            {accountNameMap?.[row.id] || ''}
            {row.recategorised_from && (
              <Pill pillRole="status" colorVariant="information" fill ml={1}>
                {intl.formatMessage(
                  {
                    id: 'common.reclassified_from',
                    defaultMessage: 'Reclassified from {categoryName}'
                  },
                  { categoryName: accountCategoryNames[row.recategorised_from as AccountCategory] }
                )}
              </Pill>
            )}
          </>
        </FlatTableRowHeader>
      )}
      <FlatTableRowHeader pt="4px" pb="4px" width={isBalanceSheet ? 300 : 120} px={isBalanceSheet ? '16px' : '24px'}>
        <DaysToPaySwitchRule
          isBalanceSheet={isBalanceSheet}
          row={row}
          accountCategoryNames={accountCategoryNames}
          onSwitchRuleClick={handleSwitchRuleClick}
          isUpdatingStructure={isUpdatingStructure}
        />
      </FlatTableRowHeader>
      <FlatTableRowHeader width={120}>
        {!row.year_to_date ? '-' : formatCurrency(Number(row.year_to_date), intl, currency || undefined, 0, 0)}
      </FlatTableRowHeader>
      <FlatTableRowHeader pt="4px" pb="4px" width={132} px="8px">
        <AccountRowActions
          isUpdatingStructure={isUpdatingStructure}
          coaType={isBalanceSheet ? COA_TYPE.BALANCE_SHEET : COA_TYPE.PROFIT_AND_LOSS}
          coaRow={row}
          drag={drag}
          index={index}
        />
      </FlatTableRowHeader>
    </FlatTableRow>
  );
};
